import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Animated, FlatList, LayoutAnimation, Pressable, RefreshControl, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import AsyncStorage from '@react-native-async-storage/async-storage';
import SkeletonRow from '@/components/SkeletonRow';
import EmptyStateInline from '@/components/EmptyStateInline';
import { useRouter, useFocusEffect } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import {
  fetchLeaderboardGlobalV1,
  fetchLeaderboardGymV1,
  type LeaderboardRowV1,
} from '@/lib/leaderboard';
import { fetchBaselineStatus } from '@/lib/baselineClient';
import type { BaselineStatus } from '@/lib/baselineGate';
// BaselineGateOverlay available if needed
import PinnedRankCard from '@/components/leaderboard/PinnedRankCard';
import RankDivisionBand, { DIVISIONS, getDivision } from '@/components/leaderboard/RankDivisionBand';
import TutorialOverlay from '@/components/TutorialOverlay';
import EnhancedLeaderboardRow from '@/components/leaderboard/EnhancedLeaderboardRow';
import LeaderboardMiniProfile from '@/components/leaderboard/LeaderboardMiniProfile';
import { TYPO } from '@/constants/typography';
import { RADIUS, FONT, TRACKING, SPACE } from '@/constants/designTokens';
import { ArrowRight, TrendingUp, ArrowUpRight, SlidersHorizontal } from 'lucide-react-native';
import { captureError } from '@/lib/errorReporting';
import RankHistoryChart from '@/components/RankHistoryChart';
import { recordRankSnapshot, getRankHistory, type RankSnapshot } from '@/lib/rankHistory';
import { getWeeklyMovers, type WeeklyMover } from '@/lib/weeklyMovers';
import { fetchGymBattleLeaderboard, type GymBattleRow } from '@/lib/gymBattle';
import { useUnits } from '@/lib/useUnits';
import { calculateDOTS, lbsToKg } from '@/lib/dotsCoefficient';
import GymDisclaimer from '@/components/GymDisclaimer';
import { useUserGyms } from '@/hooks/useUserGyms';
import GymSwitcherPills from '@/components/GymSwitcherPills';

type Scope = 'global' | 'gym' | 'gym_battle';
type WeightClass = 'all' | 'lightweight' | 'middleweight' | 'heavyweight' | 'superheavy';
type SortBy = 'strength' | 'relative' | 'duelsWon' | 'volume';
type GenderFilter = 'all' | 'male' | 'female';
type TimePeriod = 'week' | 'month' | 'year';

const SORT_STORAGE_KEY = 'duel_leaderboard_sort';
const GENDER_STORAGE_KEY = 'duel_leaderboard_gender';
const TIME_STORAGE_KEY = 'duel_leaderboard_time';
const PREV_RANKS_KEY = 'duel_leaderboard_prev_ranks';

const SORT_OPTIONS: { key: SortBy; label: string }[] = [
  { key: 'strength', label: 'Strength' },
  { key: 'relative', label: 'DOTS' },
  { key: 'volume', label: 'Volume' },
  { key: 'duelsWon', label: 'Win Rate' },
];
const GENDER_OPTIONS: { key: GenderFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'male', label: 'Men' },
  { key: 'female', label: 'Women' },
];
const TIME_OPTIONS: { key: TimePeriod; label: string }[] = [
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
  { key: 'year', label: 'Year' },
];

function getDotsScore(row: LeaderboardRowV1): number {
  const score = row.strength_score ?? 0;
  const bw = row.bodyweight ?? 0;
  const gender = (row.gender === 'female' ? 'female' : 'male') as 'male' | 'female';
  if (score <= 0 || bw <= 0) return 0;
  // strength_score is stored in lbs-equivalent; bodyweight in lbs
  return calculateDOTS(lbsToKg(score), lbsToKg(bw), gender);
}

function sortRows(rows: LeaderboardRowV1[], sortBy: SortBy): LeaderboardRowV1[] {
  const sorted = [...rows];
  switch (sortBy) {
    case 'strength':
      sorted.sort((a, b) => (b.strength_score ?? 0) - (a.strength_score ?? 0));
      break;
    case 'relative':
      sorted.sort((a, b) => getDotsScore(b) - getDotsScore(a));
      break;
    case 'duelsWon':
      sorted.sort((a, b) => (b.wins ?? 0) - (a.wins ?? 0));
      break;
    case 'volume':
      sorted.sort((a, b) => (b.total_volume ?? 0) - (a.total_volume ?? 0));
      break;
  }
  return sorted;
}

type ListItem =
  | { type: 'division'; division: typeof DIVISIONS[number] }
  | { type: 'row'; row: LeaderboardRowV1; rank: number };

function buildListItems(sorted: LeaderboardRowV1[], showDivisions = true): ListItem[] {
  const total = sorted.length;
  if (total === 0) return [];

  const items: ListItem[] = [];
  let lastDivisionIdx = -1;

  sorted.forEach((row, i) => {
    const rank = i + 1;
    const div = getDivision(rank, total, row.strength_score ?? undefined);
    const divIdx = DIVISIONS.indexOf(div);
    if (showDivisions && divIdx !== lastDivisionIdx) {
      items.push({ type: 'division', division: div });
      lastDivisionIdx = divIdx;
    }
    items.push({ type: 'row', row, rank });
  });

  return items;
}

export default function LeaderboardsTab() {
  const { theme } = useTheme();
  const { profile, refreshProfile } = useAuth();
  const router = useRouter();
  const { userUnits } = useUnits();
  const unitsRef = useRef(profile?.units ?? 'lbs');
  unitsRef.current = profile?.units ?? 'lbs';
  const [scope, setScope] = useState<Scope>('global');
  const [sortBy, setSortBy] = useState<SortBy>('strength');
  const [genderFilter, setGenderFilter] = useState<GenderFilter>('all');
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('month');
  const [rows, setRows] = useState<LeaderboardRowV1[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [baselineStatus, setBaselineStatus] = useState<BaselineStatus | null>(null);
  const [hasAnyLoggedActivity, setHasAnyLoggedActivity] = useState<boolean | null>(null);
  const [prevRanks, setPrevRanks] = useState<Record<string, number>>({});
  const [miniProfile, setMiniProfile] = useState<LeaderboardRowV1 | null>(null);
  const [rankHistory, setRankHistory] = useState<RankSnapshot[]>([]);
  const [showRankHistory, setShowRankHistory] = useState(false);
  const [weeklyMovers, setWeeklyMovers] = useState<WeeklyMover[]>([]);
  const [showMovers, setShowMovers] = useState(false);
  const [weightClass, _setWeightClass] = useState<WeightClass>('all');
  const [gymBattleRows, setGymBattleRows] = useState<GymBattleRow[]>([]);
  const [gymBattleLoading, setGymBattleLoading] = useState(false);
  const listRef = useRef<FlatList<ListItem>>(null);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const fadeAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.timing(fadeAnim, { toValue: 1, duration: 300, useNativeDriver: true }).start();
  }, []);

  const isKg = profile?.units === 'kg';
  const WEIGHT_CLASS_OPTIONS: { key: WeightClass; label: string; min: number; max: number }[] = isKg
    ? [
        { key: 'all', label: 'All', min: 0, max: 9999 },
        { key: 'lightweight', label: '<68 kg', min: 0, max: 67 },
        { key: 'middleweight', label: '68-91', min: 68, max: 91 },
        { key: 'heavyweight', label: '91-113', min: 91, max: 113 },
        { key: 'superheavy', label: '113+', min: 113, max: 9999 },
      ]
    : [
        { key: 'all', label: 'All', min: 0, max: 9999 },
        { key: 'lightweight', label: '<150 lb', min: 0, max: 149 },
        { key: 'middleweight', label: '150-200', min: 150, max: 200 },
        { key: 'heavyweight', label: '200-250', min: 200, max: 250 },
        { key: 'superheavy', label: '250+', min: 250, max: 9999 },
      ];

  // Determine the user's weight class
  const userWeight = (profile as any)?.weight as number | undefined;
  const _userWeightClass: WeightClass | null = (() => {
    if (!userWeight) return null;
    if (isKg) {
      if (userWeight < 68) return 'lightweight';
      if (userWeight <= 91) return 'middleweight';
      if (userWeight <= 113) return 'heavyweight';
      return 'superheavy';
    }
    if (userWeight < 150) return 'lightweight';
    if (userWeight <= 200) return 'middleweight';
    if (userWeight <= 250) return 'heavyweight';
    return 'superheavy';
  })();

  const { gyms: userGyms, activeGym: activeUserGym, setActiveGym: setActiveUserGym } = useUserGyms();
  const [allGymsSelected, setAllGymsSelected] = useState(false);

  // Use the gym switcher selection if user has multiple gyms; otherwise fall back to profile
  const gymId = userGyms.length >= 2
    ? (allGymsSelected ? null : (activeUserGym?.id ?? null))
    : (profile?.gym_id ?? profile?.home_gym_id ?? null);
  const userId = profile?.id ?? null;

  // Load persisted preferences
  useEffect(() => {
    AsyncStorage.getItem(SORT_STORAGE_KEY).then((val) => {
      if (val === 'strength' || val === 'duelsWon' || val === 'volume') setSortBy(val);
    }).catch(() => {});
    AsyncStorage.getItem(GENDER_STORAGE_KEY).then((val) => {
      if (val === 'all' || val === 'male' || val === 'female') {
        setGenderFilter(val);
      } else {
        // No stored preference — default to user's own gender
        const g = profile?.gender;
        if (g === 'male' || g === 'female') setGenderFilter(g);
      }
    }).catch(() => {});
    AsyncStorage.getItem(TIME_STORAGE_KEY).then((val) => {
      if (val === 'week' || val === 'month' || val === 'year') setTimePeriod(val);
    }).catch(() => {});
    AsyncStorage.getItem(PREV_RANKS_KEY).then((val) => {
      if (val) try { setPrevRanks(JSON.parse(val)); } catch {}
    }).catch(() => {});
  }, []);

  const handleSortChange = (s: SortBy) => {
    setSortBy(s);
    AsyncStorage.setItem(SORT_STORAGE_KEY, s).catch(() => {});
  };
  const handleGenderChange = (g: GenderFilter) => {
    setGenderFilter(g);
    AsyncStorage.setItem(GENDER_STORAGE_KEY, g).catch(() => {});
  };
  const handleTimeChange = (t: TimePeriod) => {
    setTimePeriod(t);
    AsyncStorage.setItem(TIME_STORAGE_KEY, t).catch(() => {});
  };

  const refreshBaseline = useCallback(async () => {
    if (profile?.id) {
      try {
        const bs = await fetchBaselineStatus(profile.id);
        setBaselineStatus(bs);
      } catch {}
    }
  }, [profile?.id]);

  const loadEligibility = useCallback(async () => {
    if (!profile?.id) { setHasAnyLoggedActivity(false); return; }
    try {
      const { count: workoutCount } = await supabase
        .from('workouts')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', profile.id);
      if ((workoutCount ?? 0) > 0) { setHasAnyLoggedActivity(true); return; }
      const { count: liftCount } = await supabase
        .from('lifts')
        .select('user_id', { count: 'exact', head: true })
        .eq('user_id', profile.id);
      setHasAnyLoggedActivity((liftCount ?? 0) > 0);
    } catch { setHasAnyLoggedActivity(false); }
  }, [profile?.id]);

  const load = useCallback(async () => {
    // Gym Battle scope
    if (scope === 'gym_battle') {
      setGymBattleLoading(true);
      const result = await fetchGymBattleLeaderboard(supabase);
      setGymBattleRows(result.rows);
      setError(result.error);
      setGymBattleLoading(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    // Always fetch volume in lbs (base unit) — client-side conversion handles display
    const viewerUnits = 'lbs';
    const result =
      scope === 'global'
        ? await fetchLeaderboardGlobalV1(supabase, { timePeriod, viewerUnits })
        : gymId
        ? await fetchLeaderboardGymV1(supabase, { gymId, timePeriod, viewerUnits })
        : { rows: [], error: null };
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    setRows(result.rows);
    setError(result.error);
    if (result.error) captureError(new Error(result.error), { context: 'leaderboards.load', scope });

    // Save current ranks for next comparison + compute weekly movers
    if (result.rows.length > 0) {
      const sorted = sortRows(result.rows, 'strength');
      const newRanks: Record<string, number> = {};
      sorted.forEach((r, i) => { newRanks[r.user_id] = i + 1; });

      // Compute weekly movers before overwriting prev ranks
      getWeeklyMovers(
        sorted.map((r) => ({
          userId: r.user_id,
          displayName: r.display_name || r.handle || 'Unknown',
          handle: r.handle ?? null,
        })),
      ).then(setWeeklyMovers).catch(() => {});

      AsyncStorage.setItem(PREV_RANKS_KEY, JSON.stringify(newRanks)).catch(() => {});

      // Record rank snapshot for history
      if (userId) {
        const userIdx = sorted.findIndex((r) => r.user_id === userId);
        if (userIdx >= 0) {
          recordRankSnapshot(userId, userIdx + 1, sorted.length, sorted[userIdx].strength_score ?? 0).catch(() => {});
        }
        getRankHistory(userId).then(setRankHistory).catch(() => {});
      }
    }

    setLoading(false);
  }, [scope, gymId, timePeriod]);

  useFocusEffect(
    useCallback(() => {
      let cancelled = false;
      (async () => {
        if (refreshProfile) await refreshProfile(true);
        if (cancelled) return;
        await refreshBaseline();
        if (cancelled) return;
        await loadEligibility();
        if (cancelled) return;
        await load();
      })();
      return () => { cancelled = true; };
    }, [scope, gymId, timePeriod, profile?.units])
  );

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  }, [load]);

  const filtered = useMemo(() => {
    let base = genderFilter === 'all' ? rows : rows.filter((r) => r.gender === genderFilter);
    // Apply weight class filter
    if (weightClass !== 'all') {
      const wc = WEIGHT_CLASS_OPTIONS.find((o) => o.key === weightClass);
      if (wc) {
        base = base.filter((r) => {
          const w = (r as any).weight as number | undefined;
          if (w == null) return false;
          return w >= wc.min && w <= wc.max;
        });
      }
    }
    return sortRows(base, sortBy);
  }, [rows, genderFilter, sortBy, weightClass]);

  // Division bands only make sense when sorting by strength score
  const listItems = useMemo(() => buildListItems(filtered, sortBy === 'strength'), [filtered, sortBy]);

  // Current user info
  const myRank = useMemo(() => {
    if (!userId) return null;
    const idx = filtered.findIndex((r) => r.user_id === userId);
    return idx >= 0 ? idx + 1 : null;
  }, [filtered, userId]);

  const myRow = useMemo(() => {
    if (!userId) return null;
    return filtered.find((r) => r.user_id === userId) ?? null;
  }, [filtered, userId]);

  const _gymRankInfo = useMemo(() => {
    if (!userId || !gymId) return null;
    const gymMembers = filtered.filter((r) => r.gym_id === gymId);
    const gymSorted = gymMembers.sort((a, b) => (b.strength_score ?? 0) - (a.strength_score ?? 0));
    const idx = gymSorted.findIndex((r) => r.user_id === userId);
    if (idx < 0) return null;
    const gymName = gymSorted[idx]?.gym_name ?? 'your gym';
    return { rank: idx + 1, total: gymSorted.length, gymName };
  }, [filtered, userId, gymId]);

  const myScore = filtered.find(r => r.user_id === userId)?.strength_score ?? undefined;
  const myDivision = useMemo(() => {
    if (myRank == null) return null;
    return getDivision(myRank, filtered.length, myScore);
  }, [myRank, filtered.length, myScore]);

  const myPreviousRank = userId ? (prevRanks[userId] ?? null) : null;

  const scrollToMyRank = useCallback(() => {
    if (myRank == null || !listRef.current) return;
    // Find the index of the user's row in listItems
    const idx = listItems.findIndex(
      (item) => item.type === 'row' && item.row.user_id === userId,
    );
    if (idx >= 0) {
      listRef.current.scrollToIndex({ index: idx, animated: true, viewPosition: 0.3 });
    }
  }, [listItems, myRank, userId]);

  const baselineIncomplete = Boolean(baselineStatus && !baselineStatus.canAppearOnLeaderboard);

  const renderItem = ({ item }: { item: ListItem }) => {
    if (item.type === 'division') {
      return (
        <RankDivisionBand
          divisionName={item.division.name}
          color={item.division.color}
          description={item.division.description}
        />
      );
    }
    const isGymMate = Boolean(gymId && item.row.gym_id === gymId && item.row.user_id !== userId);
    return (
      <EnhancedLeaderboardRow
        row={item.row}
        rank={item.rank}
        totalUsers={filtered.length}
        isCurrentUser={item.row.user_id === userId}
        previousRank={prevRanks[item.row.user_id] ?? null}
        onPress={() => setMiniProfile(item.row)}
        isGymMate={isGymMate}
        sortBy={sortBy}
        userUnits={userUnits}
      />
    );
  };

  const keyExtractor = (item: ListItem, index: number) =>
    item.type === 'division' ? `div-${item.division.name}` : `row-${item.row.user_id}`;

  // ---- Header rendered inside FlatList to avoid nested scroll ----
  const ListHeader = useMemo(() => (
    <>
      {/* Pinned rank card */}
      <PinnedRankCard
        rank={myRank}
        totalUsers={filtered.length}
        strengthScore={myRow?.strength_score ?? null}
        previousRank={myPreviousRank}
        freshnessBand={myRow?.freshness_band ?? null}
        displayName={profile?.display_name ?? profile?.handle ?? 'You'}
        onRankPress={scrollToMyRank}
        sortBy={sortBy}
        totalVolume={myRow?.total_volume ?? null}
        wins={myRow?.wins ?? null}
        losses={myRow?.losses ?? null}
        userUnits={userUnits}
        dotsScore={myRow ? getDotsScore(myRow) : null}
      />

      {/* Personal stats banner */}
      {myRank != null && myRank > 0 && filtered.length > 0 && (() => {
        const pct = Math.round((1 - myRank / filtered.length) * 100);
        const prevRank = myPreviousRank;
        // Don't show rank changes when total users < 5 — too volatile
        const climbing = filtered.length >= 5 && prevRank != null && prevRank > myRank;
        // Hide percentile when < 5 users or when user is last place (Top 100% is misleading)
        const showPercentile = filtered.length >= 5 && myRank < filtered.length;
        const percentileText = showPercentile ? ` — Top ${Math.max(1, 100 - pct)}%` : '';
        return (
          <View style={[styles.personalBanner, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}>
            <Text style={[styles.personalBannerText, { color: theme.colors.text }]}>
              Your Rank: #{myRank} of {filtered.length}{percentileText}
            </Text>
            {climbing && (
              <Text style={[styles.personalBannerClimb, { color: '#22c55e' }]}>↑ Climbing!</Text>
            )}
          </View>
        );
      })()}

      {/* Rank History — collapsible, default collapsed */}
      {rankHistory.length >= 2 && (
        <TouchableOpacity
          style={[styles.collapsibleHeader, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}
          onPress={() => setShowRankHistory(!showRankHistory)}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel={`${showRankHistory ? 'Hide' : 'Show'} rank history`}
        >
          <TrendingUp size={14} color={theme.colors.accent} />
          <Text style={[styles.collapsibleLabel, { color: theme.colors.accent, flex: 1 }]}>YOUR RANK HISTORY</Text>
          <Text style={{ color: theme.colors.textSecondary, fontSize: 16 }}>{showRankHistory ? '▲' : '▼'}</Text>
        </TouchableOpacity>
      )}
      {showRankHistory && rankHistory.length >= 2 && (
        <View style={[styles.rankHistoryContainer, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}>
          <RankHistoryChart history={rankHistory} />
        </View>
      )}

      {/* Division badge */}
      {myDivision && myRank != null && (
        <View style={[styles.divBadge, { backgroundColor: myDivision.color + '22' }]}>
          <View style={[styles.divDot, { backgroundColor: myDivision.color }]} />
          <Text style={[styles.divBadgeText, { color: myDivision.color === '#ffd700' ? '#b8860b' : myDivision.color }]}>
            Your Division: {myDivision.name}
          </Text>
        </View>
      )}

      {/* Baseline notice */}
      {baselineIncomplete && hasAnyLoggedActivity === true && baselineStatus && (
        <View style={[styles.inlineNotice, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}>
          <Text style={[styles.inlineNoticeHeading, { color: theme.colors.text }]}>Baseline Progress</Text>
          <Text style={[styles.inlineNoticeProgress, { color: theme.colors.text }]}>
            {baselineStatus.liftsLoggedEver} / 5 core lifts logged
          </Text>
          <View style={[styles.progressTrack, { backgroundColor: theme.colors.border }]}>
            <View style={[styles.progressFill, { backgroundColor: theme.colors.accent, width: `${Math.round((baselineStatus.liftsLoggedEver / 5) * 100)}%` as any }]} />
          </View>
          {(() => {
            const missing = baselineStatus.lifts
              .filter((l) => l.status === 'missing' || l.status === 'expired')
              .map((l) => l.label);
            return missing.length > 0 ? (
              <Text style={[styles.inlineNoticeText, { color: theme.colors.textSecondary }]}>
                Still needed: {missing.join(', ')}
              </Text>
            ) : null;
          })()}
          <TouchableOpacity
            style={[styles.inlineNoticeCta, { backgroundColor: theme.colors.accent }]}
            onPress={() => router.push({ pathname: '/(tabs)/record', params: { openPicker: '1' } })}
          >
            <View style={styles.improvedCtaContainer}>
              <Text style={[styles.improvedCtaText, { color: theme.colors.accentForeground }]}>COMPLETE YOUR STRENGTH ASSESSMENT</Text>
              <Text style={[styles.improvedCtaSubtitle, { color: theme.colors.accentForeground }]}>Log these 5 lifts to unlock your full ranking</Text>
            </View>
            <ArrowRight size={16} color={theme.colors.text} />
          </TouchableOpacity>
        </View>
      )}

      {/* Biggest Movers — collapsible, default collapsed */}
      {weeklyMovers.length > 0 && (
        <>
          <TouchableOpacity
            style={[styles.collapsibleHeader, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}
            onPress={() => setShowMovers(!showMovers)}
            activeOpacity={0.7}
            accessibilityRole="button"
            accessibilityLabel={`${showMovers ? 'Hide' : 'Show'} biggest movers`}
          >
            <ArrowUpRight size={14} color={theme.colors.accent} />
            <Text style={[styles.collapsibleLabel, { color: theme.colors.accent, flex: 1 }]}>
              BIGGEST MOVERS · {weeklyMovers.length} climber{weeklyMovers.length !== 1 ? 's' : ''}
            </Text>
            <Text style={{ color: theme.colors.textSecondary, fontSize: 16 }}>{showMovers ? '▲' : '▼'}</Text>
          </TouchableOpacity>
          {showMovers && (
            <View style={[styles.moversSection, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}>
              {weeklyMovers.map((m) => (
                <View key={m.userId} style={[styles.moverRow, { borderBottomColor: theme.colors.border }]}>
                  <Text style={[styles.moverRank, { color: theme.colors.accent }]}>#{m.currentRank}</Text>
                  <View style={{ flex: 1 }}>
                    <Text style={[styles.moverName, { color: theme.colors.text }]} numberOfLines={1}>
                      {m.displayName}
                    </Text>
                  </View>
                  <View style={styles.moverBadge}>
                    <ArrowUpRight size={14} color={theme.colors.success} />
                    <Text style={[styles.moverChange, { color: theme.colors.success }]}>
                      +{m.rankChange}
                    </Text>
                  </View>
                </View>
              ))}
            </View>
          )}
        </>
      )}
    </>
  ), [myRank, filtered.length, myRow, myPreviousRank, profile, showRankHistory, rankHistory, myDivision, baselineIncomplete, hasAnyLoggedActivity, baselineStatus, weeklyMovers, showMovers, theme, scrollToMyRank]);

  return (
    <SafeAreaView style={{ backgroundColor: theme.colors.background, flex: 1 }} edges={['top']}>
      <Animated.View style={[styles.container, { opacity: fadeAnim }]}>
        {/* Scope toggle */}
        <View style={styles.scopeRow}>
          {(['global', 'gym', 'gym_battle'] as const).map((s) => {
            const label = s === 'global' ? 'Global' : s === 'gym' ? 'My Gym' : 'Gym Battle';
            return (
            <Pressable
              key={s}
              style={[styles.scopeButton, 
                { borderColor: theme.colors.border },
                scope === s && { borderBottomColor: theme.colors.accent }
              ]}
              onPress={() => setScope(s)}
              accessibilityRole="button"
              accessibilityLabel={`${label} leaderboard`}
              accessibilityState={{ selected: scope === s }}
              android_ripple={{ color: theme.colors.accent + '20', borderless: false }}
            >
              <Text style={[styles.scopeText, { color: scope === s ? theme.colors.accent : theme.colors.textSecondary }]}>
                {label}
              </Text>
            </Pressable>
            );
          })}
        </View>

        {/* Multi-gym switcher */}
        {scope === 'gym' && userGyms.length >= 2 && (
          <View style={{ paddingHorizontal: 16, paddingTop: 8 }}>
            <GymSwitcherPills
              gyms={userGyms}
              activeGymId={activeUserGym?.id ?? null}
              onSelect={(gym) => { setAllGymsSelected(false); setActiveUserGym(gym); }}
              colors={theme.colors}
              showAll
              allSelected={allGymsSelected}
              onSelectAll={() => setAllGymsSelected(true)}
            />
          </View>
        )}

        {/* Gym name banner + disclaimer */}
        {scope === 'gym' && gymId && (activeUserGym?.name || profile?.home_gym_name) && (
          <View style={{ paddingHorizontal: 16, paddingVertical: 8, gap: 6 }}>
            <View style={{ backgroundColor: theme.colors.accent + '15', borderRadius: 4, paddingVertical: 10, paddingHorizontal: 14, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <Text style={{ fontSize: 16, fontWeight: '700', color: theme.colors.accent, flex: 1 }}>🏋️ {activeUserGym?.name || profile?.home_gym_name}</Text>
            </View>
            <GymDisclaimer gymName={activeUserGym?.name || profile?.home_gym_name || ''} />
          </View>
        )}

        {/* Filters toggle */}
        <View style={styles.filtersToggleRow}>
          <TouchableOpacity
            style={[styles.filtersToggleBtn, { borderColor: theme.colors.border, backgroundColor: filtersExpanded ? theme.colors.accent + '12' : 'transparent' }]}
            onPress={() => {
              LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
              setFiltersExpanded((prev) => !prev);
            }}
            accessibilityRole="button"
            accessibilityLabel={filtersExpanded ? 'Hide filters' : 'Show filters'}
          >
            <SlidersHorizontal size={14} color={filtersExpanded ? theme.colors.accent : theme.colors.textSecondary} />
            <Text style={[styles.filtersToggleText, { color: filtersExpanded ? theme.colors.accent : theme.colors.textSecondary }]}>
              Filters
            </Text>
            <Text style={{ color: theme.colors.textSecondary, fontSize: 12 }}>{filtersExpanded ? '▲' : '▼'}</Text>
          </TouchableOpacity>
          {/* Active filter summary when collapsed */}
          {!filtersExpanded && (
            <Text style={[styles.filterSummary, { color: theme.colors.textSecondary }]}>
              {SORT_OPTIONS.find((o) => o.key === sortBy)?.label} · {GENDER_OPTIONS.find((o) => o.key === genderFilter)?.label} · {TIME_OPTIONS.find((o) => o.key === timePeriod)?.label}
            </Text>
          )}
        </View>

        {/* Filter pills — collapsible */}
        {filtersExpanded && (
          <View style={styles.sortRow}>
            {SORT_OPTIONS.map((opt) => (
              <Pressable
                key={opt.key}
                style={[styles.sortPill, { borderColor: theme.colors.border }, sortBy === opt.key && { backgroundColor: theme.colors.accent + '22', borderColor: theme.colors.accent }]}
                onPress={() => handleSortChange(opt.key)}
                accessibilityRole="button"
                accessibilityLabel={`Sort by ${opt.label}`}
                accessibilityState={{ selected: sortBy === opt.key }}
                android_ripple={{ color: theme.colors.accent + '20', borderless: false }}
              >
                <Text style={[styles.sortPillText, { color: sortBy === opt.key ? theme.colors.accent : theme.colors.textSecondary }]}>{opt.label}</Text>
              </Pressable>
            ))}
            <View style={{ width: 1, backgroundColor: theme.colors.border, marginVertical: 2 }} />
            {GENDER_OPTIONS.map((opt) => (
              <Pressable
                key={opt.key}
                style={[styles.sortPill, { borderColor: theme.colors.border }, genderFilter === opt.key && { backgroundColor: theme.colors.accent + '22', borderColor: theme.colors.accent }]}
                onPress={() => handleGenderChange(opt.key)}
                accessibilityRole="button"
                accessibilityLabel={`Filter by ${opt.label}`}
                accessibilityState={{ selected: genderFilter === opt.key }}
                android_ripple={{ color: theme.colors.accent + '20', borderless: false }}
              >
                <Text style={[styles.sortPillText, { color: genderFilter === opt.key ? theme.colors.accent : theme.colors.textSecondary }]}>{opt.label}</Text>
              </Pressable>
            ))}
            <View style={{ width: 1, backgroundColor: theme.colors.border, marginVertical: 2 }} />
            {TIME_OPTIONS.map((opt) => (
              <Pressable
                key={opt.key}
                style={[styles.sortPill, { borderColor: theme.colors.border }, timePeriod === opt.key && { backgroundColor: theme.colors.accent + '22', borderColor: theme.colors.accent }]}
                onPress={() => handleTimeChange(opt.key)}
                accessibilityRole="button"
                accessibilityLabel={`Time period: ${opt.label}`}
                accessibilityState={{ selected: timePeriod === opt.key }}
                android_ripple={{ color: theme.colors.accent + '20', borderless: false }}
              >
                <Text style={[styles.sortPillText, { color: timePeriod === opt.key ? theme.colors.accent : theme.colors.textSecondary }]}>{opt.label}</Text>
              </Pressable>
            ))}
          </View>
        )}

        {/* Gym Battle List */}
        {scope === 'gym_battle' ? (
          gymBattleLoading ? (
            <SkeletonRow rows={6} />
          ) : gymBattleRows.length === 0 ? (
            <EmptyStateInline icon="🏆" title="Gym Battle Coming Soon" description="As more gyms get members ranked, they'll appear here for head-to-head competition." />
          ) : (
            <FlatList
              data={gymBattleRows}
              keyExtractor={(item) => item.gym_id}
              contentContainerStyle={styles.listContent}
              refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={theme.colors.accent} />}
              renderItem={({ item, index }) => (
                <View style={[styles.gymBattleRow, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}>
                  <Text style={[styles.gymBattleRank, { color: theme.colors.accent }]}>#{index + 1}</Text>
                  <View style={{ flex: 1 }}>
                    <Text style={[styles.gymBattleName, { color: theme.colors.text }]} numberOfLines={1}>{item.gym_name}</Text>
                    <Text style={[styles.gymBattleMeta, { color: theme.colors.textSecondary }]}>
                      {item.member_count} member{item.member_count !== 1 ? 's' : ''} · avg {item.avg_strength} pts
                    </Text>
                  </View>
                  <Text style={[styles.gymBattleScore, { color: theme.colors.text }]}>{item.avg_strength}</Text>
                </View>
              )}
            />
          )
        ) : scope === 'gym' && !gymId ? (
          <EmptyStateInline
            icon="🏋️"
            title="Set Your Home Gym"
            description="Claim your gym to see where you rank against members and compete on the gym leaderboard."
            actionLabel="Find My Gym"
            onAction={() => router.push('/(tabs)/edit-profile')}
          />
        ) : loading && !refreshing ? (
          <SkeletonRow rows={8} />
        ) : error ? (
          <EmptyStateInline icon="⚠️" title="Couldn't load data" description="Something went wrong. Pull down to retry." />
        ) : filtered.length === 0 ? (
          <EmptyStateInline
            icon="—"
            title="No ranked members yet"
            description="Log your 5 core lifts to get ranked on the leaderboard"
          />
        ) : (
          <FlatList
            ref={listRef}
            data={listItems}
            renderItem={renderItem}
            keyExtractor={keyExtractor}
            ListHeaderComponent={ListHeader}
            contentContainerStyle={styles.listContent}
            windowSize={5}
            maxToRenderPerBatch={10}
            initialNumToRender={15}
            removeClippedSubviews={true}
            getItemLayout={(_data, index) => ({ length: 60, offset: 60 * index, index })}
            refreshControl={
              <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={theme.colors.accent} />
            }
            onScrollToIndexFailed={(info) => {
              // Fallback: scroll to approximate offset then retry
              listRef.current?.scrollToOffset({ offset: info.averageItemLength * info.index, animated: true });
            }}
          />
        )}

        {/* Mini profile modal */}
        {miniProfile && (
          <LeaderboardMiniProfile
            visible={!!miniProfile}
            onClose={() => setMiniProfile(null)}
            userId={miniProfile.user_id}
            displayName={miniProfile.display_name}
            handle={miniProfile.handle}
            strengthScore={miniProfile.strength_score}
            freshnessBand={miniProfile.freshness_band}
            rank={miniProfile.rank}
            wins={miniProfile.wins}
            losses={miniProfile.losses}
          />
        )}
      </Animated.View>
      <TutorialOverlay screenKey="rank" />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  scopeRow: {
    flexDirection: 'row',
    gap: SPACE.sm + 2,
    paddingHorizontal: SPACE.md,
    paddingTop: SPACE.md,
    paddingBottom: SPACE.base,
  },
  scopeButton: {
    flex: 1,
    paddingVertical: 16,
    borderRadius: RADIUS.none,
    borderWidth: 1,
    borderBottomWidth: 3,
    alignItems: 'center',
  },
  scopeText: { fontSize: 14, fontFamily: TYPO.subheading.bold, textTransform: 'uppercase', letterSpacing: TRACKING.wider },
  filtersToggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: SPACE.md,
    paddingTop: SPACE.sm,
    paddingBottom: SPACE.xs,
    gap: SPACE.sm + 2,
  },
  filtersToggleBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: RADIUS.xs,
    borderWidth: 1,
  },
  filtersToggleText: {
    fontSize: 13,
    fontFamily: TYPO.subheading.bold,
    textTransform: 'uppercase',
    letterSpacing: TRACKING.wide,
  },
  filterSummary: {
    fontSize: 12,
    fontFamily: TYPO.body.medium,
    flex: 1,
  },
  sortRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: SPACE.sm,
    paddingHorizontal: SPACE.md,
    paddingTop: SPACE.sm,
    paddingBottom: SPACE.md,
    alignItems: 'center',
  },
  sortPill: {
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: RADIUS.none,
    borderWidth: 1,
  },
  sortPillText: { fontSize: 11, fontFamily: TYPO.subheading.bold, textTransform: 'uppercase', letterSpacing: TRACKING.wide },
  divBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'center',
    gap: 6,
    paddingVertical: 4,
    paddingHorizontal: 12,
    borderRadius: 2,
    marginVertical: 4,
  },
  divDot: { width: 8, height: 8, borderRadius: 4 },
  divBadgeText: { fontSize: 12, fontFamily: TYPO.heading.bold },
  personalBanner: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 12, borderWidth: 1, borderRadius: RADIUS.xs, marginBottom: SPACE.sm },
  personalBannerText: { fontSize: FONT.sm, fontFamily: TYPO.subheading.semibold },
  personalBannerClimb: { fontSize: FONT.sm, fontFamily: TYPO.subheading.bold },
  collapsibleHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginHorizontal: 16,
    marginTop: 8,
    marginBottom: 8,
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: RADIUS.xs,
    borderWidth: 1,
  },
  collapsibleLabel: {
    fontSize: 13,
    fontFamily: TYPO.subheading.bold,
    textTransform: 'uppercase',
    letterSpacing: TRACKING.widest,
  },
  rankHistoryContainer: {
    marginHorizontal: 16,
    marginBottom: 8,
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderRadius: RADIUS.xs,
    borderWidth: 1,
  },
  listContent: { paddingHorizontal: SPACE.md, paddingBottom: SPACE.xl },
  moversSection: {
    marginHorizontal: 16,
    marginBottom: 8,
    borderRadius: RADIUS.xs,
    borderWidth: 1,
    paddingHorizontal: 14,
    paddingVertical: 8,
  },
  moverRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 6,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  moverRank: {
    fontSize: 14,
    fontFamily: TYPO.heading.bold,
    width: 36,
  },
  moverName: {
    fontSize: 14,
    fontFamily: TYPO.body.medium,
  },
  moverBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  moverChange: {
    fontSize: 14,
    fontFamily: TYPO.body.bold,
  },
  gymBattleRow: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    gap: 16,
    paddingVertical: 16,
    paddingHorizontal: 16,
    borderRadius: RADIUS.none,
    borderBottomWidth: 1,
    marginBottom: 0,
    minHeight: 56,
  },
  gymBattleRank: {
    fontSize: 16,
    fontFamily: TYPO.heading.bold,
    width: 36,
  },
  gymBattleName: {
    fontSize: 14,
    fontFamily: TYPO.body.bold,
  },
  gymBattleMeta: {
    fontSize: 11,
    fontFamily: TYPO.body.medium,
    marginTop: 2,
  },
  gymBattleScore: {
    fontSize: 18,
    fontFamily: TYPO.heading.bold,
    textAlign: 'right',
  },
  inlineNotice: {
    marginHorizontal: 16,
    marginTop: 8,
    marginBottom: 8,
    borderRadius: RADIUS.xs,
    borderWidth: 1,
    paddingHorizontal: 14,
    paddingVertical: 14,
    gap: 8,
  },
  inlineNoticeHeading: { fontSize: FONT.base + 1, fontFamily: TYPO.heading.bold, letterSpacing: 0.2 },
  inlineNoticeProgress: { fontSize: FONT.lg, fontFamily: TYPO.heading.bold },
  progressTrack: { height: 8, borderRadius: 4, overflow: 'hidden' as const },
  progressFill: { height: 8, borderRadius: 4, minWidth: 8 },
  inlineNoticeText: { fontSize: FONT.sm + 1, fontFamily: TYPO.body.medium },
  inlineNoticeCta: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 18,
    borderRadius: RADIUS.none,
    marginTop: 4,
  },
  inlineNoticeCtaText: { fontSize: FONT.base, fontFamily: TYPO.heading.bold },
  improvedCtaContainer: { alignItems: 'center', gap: SPACE.xs },
  improvedCtaText: { fontSize: FONT.base, fontFamily: TYPO.heading.bold, textAlign: 'center' },
  improvedCtaSubtitle: { fontSize: FONT.xs, fontFamily: TYPO.body.semibold, textAlign: 'center', opacity: 0.8 },
  gymRankBadge: {
    marginHorizontal: 16,
    marginTop: 4,
    marginBottom: 4,
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 2,
    borderWidth: 1,
    alignItems: 'center' as const,
  },
  gymRankText: {
    fontSize: 14,
    fontFamily: TYPO.heading.bold,
  },
});
